import React, { useState, useEffect, useRef } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import poofAnimation from "../assets/poof2.json";
import swooshSound from "../assets/swoosh.mp3";
import apiService from "../services/api";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerSwitchPlaylist = () => {
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [useLunaKey, setUseLunaKey] = useState(false);
  const [useM3UPlaylist, setUseM3UPlaylist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    url: "https://",
    username: "",
    password: "",
    lunaKey: "",
    playlistUrl: "https://",
  });
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showModifyDialog, setShowModifyDialog] = useState(false);
  const [selectedForModify, setSelectedForModify] = useState(null);
  const [deletingPlaylist, setDeletingPlaylist] = useState(null);
  const [showDeleteAnimation, setShowDeleteAnimation] = useState(false);
  const audioRef = useRef(null);

  // Load saved playlists on mount
  useEffect(() => {
    loadSavedPlaylists();
  }, []);

  const loadSavedPlaylists = () => {
    const currentToken = webPlayerStorage.getToken();
    const currentPlaylist = webPlayerStorage.getPlaylist();

    const saved = localStorage.getItem("savedPlaylists");
    let loadedPlaylists = [];

    if (saved) {
      try {
        loadedPlaylists = JSON.parse(saved);
      } catch (error) {
        console.error("Error loading playlists:", error);
      }
    }

    // If we have a current token and playlist, ensure it's in the list and marked as active
    if (currentToken && currentPlaylist && currentPlaylist.id) {
      const existingIndex = loadedPlaylists.findIndex(
        (p) => p.id === currentPlaylist.id
      );

      if (existingIndex !== -1) {
        // Mark the current playlist as active, others as inactive
        loadedPlaylists = loadedPlaylists.map((p, index) => ({
          ...p,
          isActive: index === existingIndex,
        }));
      } else {
        // Add current playlist to the list and mark as active
        loadedPlaylists = [
          {
            id: currentPlaylist.id,
            name: currentPlaylist.name || "Current Playlist",
            token: currentToken,
            type: "standard",
            isActive: true,
          },
          ...loadedPlaylists.map((p) => ({ ...p, isActive: false })),
        ];
        // Save updated list
        localStorage.setItem("savedPlaylists", JSON.stringify(loadedPlaylists));
      }
    }

    setPlaylists(loadedPlaylists);
  };

  const savePlaylistsToStorage = (updatedPlaylists) => {
    localStorage.setItem("savedPlaylists", JSON.stringify(updatedPlaylists));
    setPlaylists(updatedPlaylists);
  };

  const selectNewPlaylist = async (playlist) => {
    try {
      // Store the new token
      webPlayerStorage.setToken(playlist.token);

      // Store playlist info
      webPlayerStorage.setPlaylist({
        id: playlist.id,
        name: playlist.name,
      });

      // CRITICAL: Fetch fresh profiles for this new playlist
      const response = await apiService.webPlayerRequest("/v2/user/profiles");

      // Clear old loginAuth and store new data
      webPlayerStorage.setLoginAuth({
        playlist: {
          playlistId: playlist.id,
          playlistName: playlist.name,
        },
        token: playlist.token,
        profiles: response.data || [],
      });

      // Navigate to profiles page with fresh data
      navigate("/web-player/profiles");
    } catch (error) {
      toast.error("Failed to switch to new playlist");
      console.error(error);
    }
  };

  const handleAddNewPlaylist = () => {
    setEditingPlaylist(null);
    setFormData({
      url: "https://",
      username: "",
      password: "",
      lunaKey: "",
      playlistUrl: "https://",
    });
    setUseLunaKey(false);
    setUseM3UPlaylist(false);
    setShowAddForm(true);
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingPlaylist(null);
    setFormData({
      url: "https://",
      username: "",
      password: "",
      lunaKey: "",
      playlistUrl: "https://",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (useM3UPlaylist) {
      if (!formData.playlistUrl || formData.playlistUrl === "https://") {
        toast.error("Please enter a valid M3U playlist URL");

        return;
      }

      setLoading(true);
      try {
        const response = await apiService.webPlayerRequest(
          "/v2/login/playlist",
          {
            method: "POST",
            body: JSON.stringify({
              playlist: formData.playlistUrl,
            }),
          }
        );

        if (!response.auth || response.status !== 200) {
          throw new Error("Invalid M3U playlist URL");
        }

        savePlaylist(
          response.playlist.playlistId,
          response.playlist.playlistName,
          response.token,
          "m3u",
          { playlistUrl: formData.playlistUrl }
        );
        toast.success(
          editingPlaylist
            ? "Playlist Modified Successfully"
            : "Playlist Added Successfully"
        );

        // If adding a new playlist (not editing), automatically select it
        if (!editingPlaylist) {
          await selectNewPlaylist({
            id: response.playlist.playlistId,
            name: response.playlist.playlistName,
            token: response.token,
          });
        } else {
          handleCancelForm();
        }
      } catch (error) {
        toast.error(error.message || "Failed to connect to M3U playlist");
      } finally {
        setLoading(false);
      }
    } else if (useLunaKey) {
      if (
        !formData.lunaKey ||
        formData.lunaKey.replace(/-/g, "").length !== 12
      ) {
        toast.error("Please enter a valid LunaKey");

        return;
      }

      setLoading(true);
      try {
        const response = await apiService.webPlayerLogin({
          code: formData.lunaKey,
        });

        if (!response.auth || response.status !== 200) {
          throw new Error("Invalid LunaKey credentials");
        }

        const lunaPlaylist =
          response.playlist || response.playlists?.[0] || null;

        savePlaylist(
          lunaPlaylist?.playlistId || lunaPlaylist?.id,
          lunaPlaylist?.playlistName || lunaPlaylist?.name,
          response.token,
          "lunakey",
          { lunaKey: formData.lunaKey }
        );
        toast.success(
          editingPlaylist
            ? "Playlist Modified Successfully"
            : "Playlist Added Successfully"
        );

        // If adding a new playlist (not editing), automatically select it
        if (!editingPlaylist) {
          await selectNewPlaylist({
            id: lunaPlaylist?.playlistId || lunaPlaylist?.id,
            name: lunaPlaylist?.playlistName || lunaPlaylist?.name,
            token: response.token,
          });
        } else {
          handleCancelForm();
        }
      } catch (error) {
        toast.error(error.message || "LunaKey authentication failed");
      } finally {
        setLoading(false);
      }
    } else {
      if (!formData.url || !formData.username || !formData.password) {
        toast.error("Please fill in all fields");

        return;
      }

      setLoading(true);
      try {
        const response = await apiService.webPlayerLogin({
          url: formData.url,
          username: formData.username,
          password: formData.password,
        });

        if (!response.auth || response.status !== 200) {
          throw new Error("Invalid credentials");
        }

        const stdPlaylist =
          response.playlist || response.playlists?.[0] || null;

        savePlaylist(
          stdPlaylist?.playlistId || stdPlaylist?.id,
          stdPlaylist?.playlistName || stdPlaylist?.name,
          response.token,
          "standard",
          { url: formData.url, username: formData.username }
        );
        toast.success(
          editingPlaylist
            ? "Playlist Modified Successfully"
            : "Playlist Added Successfully"
        );

        // If adding a new playlist (not editing), automatically select it
        if (!editingPlaylist) {
          await selectNewPlaylist({
            id: stdPlaylist?.playlistId || stdPlaylist?.id,
            name: stdPlaylist?.playlistName || stdPlaylist?.name,
            token: response.token,
          });
        } else {
          handleCancelForm();
        }
      } catch (error) {
        toast.error(error.message || "Login failed. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const savePlaylist = (
    playlistId,
    playlistName,
    authToken,
    type,
    credentials = {}
  ) => {
    const existingIndex = playlists.findIndex((p) => p.id === playlistId);
    let updatedPlaylists;

    if (existingIndex !== -1) {
      // Update existing playlist
      updatedPlaylists = [...playlists];
      updatedPlaylists[existingIndex] = {
        id: playlistId,
        name: playlistName,
        token: authToken,
        type,
        isActive: updatedPlaylists[existingIndex].isActive,
        credentials, // Store connection details (but not password)
      };

      // If updating the active playlist, update the token in storage
      if (updatedPlaylists[existingIndex].isActive) {
        webPlayerStorage.setToken(authToken);
        webPlayerStorage.setPlaylist({
          id: playlistId,
          name: playlistName,
        });
      }
    } else {
      // Add new playlist (not active by default)
      updatedPlaylists = [
        ...playlists,
        {
          id: playlistId,
          name: playlistName,
          token: authToken,
          type,
          isActive: false,
          credentials, // Store connection details (but not password)
        },
      ];
    }

    savePlaylistsToStorage(updatedPlaylists);
  };

  const handleSelectPlaylist = async (playlist) => {
    setLoading(true);
    try {
      // Store token and playlist info
      webPlayerStorage.setToken(playlist.token);
      webPlayerStorage.setPlaylist({
        id: playlist.id,
        name: playlist.name,
      });

      // Mark playlist as active
      const updatedPlaylists = playlists.map((p) => ({
        ...p,
        isActive: p.id === playlist.id,
      }));

      savePlaylistsToStorage(updatedPlaylists);

      // CRITICAL: Fetch fresh profiles for this playlist
      const response = await apiService.webPlayerRequest("/v2/user/profiles");

      // Clear old loginAuth and store new data for this playlist
      webPlayerStorage.setLoginAuth({
        playlist: {
          playlistId: playlist.id,
          playlistName: playlist.name,
        },
        token: playlist.token,
        profiles: response.data || [],
      });

      // Navigate to profiles page
      navigate("/web-player/profiles");
    } catch (error) {
      toast.error("Failed to switch playlist");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLongPress = (playlist, e) => {
    e.preventDefault();
    setSelectedForModify(playlist);
    setShowModifyDialog(true);
  };

  const handleContextMenu = (playlist, e) => {
    e.preventDefault();
    setSelectedForModify(playlist);
    setShowModifyDialog(true);
  };

  const handleEditPlaylist = async () => {
    if (selectedForModify) {
      setEditingPlaylist(selectedForModify);

      let url = "https://";
      let username = "";
      let lunaKey = "";
      let playlistUrl = "https://";

      // Try to get credentials from stored credentials first
      const credentials = selectedForModify.credentials || {};
      const {
        lunaKey: credentialLunaKey,
        playlistUrl: credentialPlaylistUrl,
        url: credentialUrl,
        username: credentialUsername,
      } = credentials;

      if (credentialUrl) {
        url = credentialUrl;
      }
      if (credentialUsername) {
        username = credentialUsername;
      }
      if (credentialLunaKey) {
        lunaKey = credentialLunaKey;
      }
      if (credentialPlaylistUrl) {
        playlistUrl = credentialPlaylistUrl;
      }

      // If no stored credentials, try to fetch from API
      if (!credentials.url && selectedForModify.token) {
        try {
          // Temporarily set the token to make the API call
          const currentToken = webPlayerStorage.getToken();

          webPlayerStorage.setToken(selectedForModify.token);

          // Fetch user info to get username
          const response = await apiService.webPlayerRequest("/v2/user/info");

          if (response?.data?.username) {
            ({ username } = response.data);
          }

          // Restore original token
          webPlayerStorage.setToken(currentToken);
        } catch (error) {
          console.error("Error fetching user info:", error);
          // Restore original token even on error
          const currentToken = webPlayerStorage.getToken();

          webPlayerStorage.setToken(currentToken);
        }
      }

      // Extract domain from playlist name
      // Playlist names are in format: "domain - accountName" (e.g., "m1pro1.com - amigo")
      if (!credentials.url && selectedForModify.name) {
        const nameParts = selectedForModify.name.split(" - ");

        if (nameParts.length > 0) {
          const domain = nameParts[0].trim();

          if (domain) {
            url = domain.startsWith("http") ? domain : `https://${domain}`;
          }
        }
      }

      setFormData({
        url,
        username,
        password: "", // Don't pre-fill password for security
        lunaKey,
        playlistUrl,
      });

      setUseLunaKey(selectedForModify.type === "lunakey");
      setUseM3UPlaylist(selectedForModify.type === "m3u");
      setShowAddForm(true);
      setShowModifyDialog(false);
    }
  };

  const handleDeletePlaylist = () => {
    if (selectedForModify) {
      // Set the playlist being deleted and show animation
      setDeletingPlaylist(selectedForModify.id);
      setShowDeleteAnimation(true);
      setShowModifyDialog(false);

      // Play swoosh sound
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch((error) => {
          console.error("Error playing sound:", error);
        });
      }

      // Wait for animation to complete before actually deleting
      setTimeout(() => {
        const updatedPlaylists = playlists.filter(
          (p) => p.id !== selectedForModify.id
        );

        if (updatedPlaylists.length === 0) {
          // If no playlists left, clear everything and go to login
          webPlayerStorage.clearAll();
          localStorage.removeItem("savedPlaylists");
          toast.info("All playlists deleted");
          navigate("/web-player/login");
        } else {
          savePlaylistsToStorage(updatedPlaylists);
          toast.success("Playlist deleted successfully");
        }

        setDeletingPlaylist(null);
        setShowDeleteAnimation(false);
        setSelectedForModify(null);
      }, 800); // Animation duration
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Audio element for delete sound */}
      <audio preload="auto" ref={audioRef} src={swooshSound} />

      {/* Dashboard Button */}
      <motion.button
        animate={{ opacity: 0.4, y: 0 }}
        className="fixed bottom-8 right-8 z-20 text-white text-sm font-medium bg-black/50 px-4 py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        initial={{ opacity: 0, y: 20 }}
        transition={{ delay: 0.5, duration: 0.3 }}
        whileHover={{ opacity: 1, scale: 1.05 }}
        onClick={() => {
          // Clear all session data
          webPlayerStorage.clearAll();
          // Redirect to dashboard
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </motion.button>

      {/* Logo - Always centered */}
      <div className="absolute top-0 left-0 right-0 flex justify-center mt-8 mb-12 z-20">
        <img
          alt="Lunova Logo"
          className="h-32 w-auto"
          src="/logo.png"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-6xl px-4 flex gap-12 mt-48 mx-auto">
        {/* Left Column - Playlists and Button */}
        <motion.div
          animate={{ opacity: 1, x: 0 }}
          className="flex-1 flex flex-col justify-between min-h-[60vh]"
          initial={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.5 }}
        >
          {/* Top Section */}
          <div>
            <div className="text-center mb-12">
              <h1 className="text-3xl font-light text-white mb-2">
                Current Playlists
              </h1>
              <p className="text-gray-400 text-sm">Select a playlist to load</p>
              <p className="text-gray-400 text-sm">
                Press and hold to modify a playlist
              </p>
            </div>

            {/* Playlist List */}
            <div
              className={`space-y-4 max-h-96 overflow-y-auto mb-6 transition-all duration-500 ${showAddForm ? "w-full" : "w-2/5 mx-auto"}`}
            >
              <AnimatePresence>
                {playlists.length > 0 ? (
                  playlists.map((playlist, index) => (
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      className={`px-8 py-5 rounded-full cursor-pointer transition-all duration-300 ${
                        playlist.isActive
                          ? "bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-500/30"
                          : "bg-gray-800/60 backdrop-blur-md border border-gray-600/50 hover:border-blue-500/50"
                      }`}
                      exit={{ opacity: 0, y: -10 }}
                      initial={{ opacity: 0, y: 10 }}
                      key={playlist.id}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleSelectPlaylist(playlist)}
                      onContextMenu={(e) => handleContextMenu(playlist, e)}
                      onMouseDown={(e) => {
                        const timer = setTimeout(
                          () => handleLongPress(playlist, e),
                          500
                        );

                        setLongPressTimer(timer);
                      }}
                      onMouseLeave={() => {
                        if (longPressTimer) {
                          clearTimeout(longPressTimer);
                          setLongPressTimer(null);
                        }
                      }}
                      onMouseUp={() => {
                        if (longPressTimer) {
                          clearTimeout(longPressTimer);
                          setLongPressTimer(null);
                        }
                      }}
                    >
                      <div className="flex items-center justify-center relative overflow-hidden">
                        <span
                          className={`text-lg font-medium truncate max-w-full px-2 ${playlist.isActive ? "text-white" : "text-gray-300"}`}
                        >
                          {playlist.isActive && "✓ "}
                          {playlist.name}
                        </span>

                        {/* Delete Animation Overlay */}
                        {deletingPlaylist === playlist.id &&
                          showDeleteAnimation && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
                              <Lottie
                                animationData={poofAnimation}
                                loop={false}
                                style={{
                                  width: "200px",
                                  height: "200px",
                                }}
                              />
                            </div>
                          )}
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <motion.div
                    animate={{ opacity: 1 }}
                    className="text-center text-gray-400 text-sm py-8"
                    initial={{ opacity: 0 }}
                  >
                    No playlists saved yet. Add one below to get started.
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Bottom Section - Add New Playlist Button */}
          {!showAddForm && (
            <div className="mb-8">
              <motion.button
                animate={{ scale: 1 }}
                className="w-2/5 mx-auto px-5 py-3 rounded-full bg-gray-800/60 backdrop-blur-md border border-gray-600/50 hover:border-purple-500/50 text-gray-300 hover:text-white transition-all duration-500 flex items-center justify-center gap-2"
                initial={{ scale: 0.9 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAddNewPlaylist}
              >
                <i className="fas fa-plus text-sm" />
                <span className="text-sm font-medium">Add New Playlist</span>
              </motion.button>
            </div>
          )}
        </motion.div>

        {/* Right Column - Add/Edit Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              className="flex-1"
              exit={{ opacity: 0, x: 20 }}
              initial={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-12">
                <h1 className="text-3xl font-light text-white mb-2">
                  {editingPlaylist
                    ? "Edit Your Playlist"
                    : "Enter New Playlist"}
                </h1>
              </div>

              <form
                className="space-y-4"
                key={editingPlaylist?.id || "new"}
                onSubmit={handleSubmit}
              >
                {/* M3U Playlist Toggle */}
                <div className="relative">
                  <motion.button
                    className={`w-full px-8 py-5 rounded-full flex items-center justify-between transition-all duration-300 ${
                      useM3UPlaylist
                        ? "bg-gradient-to-r from-green-500 to-green-600 shadow-lg shadow-green-500/30"
                        : "bg-gray-800/60 backdrop-blur-md border border-gray-600/50"
                    }`}
                    type="button"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => {
                      setUseM3UPlaylist(!useM3UPlaylist);
                      if (!useM3UPlaylist) {
                        setUseLunaKey(false);
                      }
                    }}
                  >
                    <span
                      className={`text-lg font-medium ${useM3UPlaylist ? "text-white" : "text-gray-300"}`}
                    >
                      Use M3U Playlist
                    </span>
                    <div
                      className={`relative w-14 h-8 ${useM3UPlaylist ? "bg-white/30" : "bg-gray-700"} rounded-full p-1 transition-colors duration-300`}
                    >
                      <motion.div
                        animate={{ x: useM3UPlaylist ? 22 : 0 }}
                        className={`w-6 h-6 ${useM3UPlaylist ? "bg-white" : "bg-gray-400"} rounded-full shadow-md`}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 30,
                        }}
                      />
                    </div>
                  </motion.button>
                </div>

                {/* M3U URL Input */}
                <AnimatePresence>
                  {useM3UPlaylist && (
                    <motion.div
                      animate={{ opacity: 1, height: "auto" }}
                      className="relative"
                      exit={{ opacity: 0, height: 0 }}
                      initial={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <i className="fas fa-earth-americas absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                      <input
                        className="w-full pl-12 pr-4 py-4 bg-white/95 backdrop-blur-sm rounded-full text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition-all shadow-lg"
                        disabled={loading}
                        name="playlistUrl"
                        placeholder="https://"
                        type="text"
                        value={formData.playlistUrl}
                        onChange={handleInputChange}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* LunaKey Toggle */}
                {!useM3UPlaylist && (
                  <div className="relative">
                    <motion.button
                      className={`w-full px-8 py-5 rounded-full flex items-center justify-between transition-all duration-300 ${
                        useLunaKey
                          ? "bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-500/30"
                          : "bg-gray-800/60 backdrop-blur-md border border-gray-600/50"
                      }`}
                      type="button"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => setUseLunaKey(!useLunaKey)}
                    >
                      <span
                        className={`text-lg font-medium ${useLunaKey ? "text-white" : "text-gray-300"}`}
                      >
                        Use LunaKey™
                      </span>
                      <div
                        className={`relative w-14 h-8 ${useLunaKey ? "bg-white/30" : "bg-gray-700"} rounded-full p-1 transition-colors duration-300`}
                      >
                        <motion.div
                          animate={{ x: useLunaKey ? 22 : 0 }}
                          className={`w-6 h-6 ${useLunaKey ? "bg-white" : "bg-gray-400"} rounded-full shadow-md`}
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 30,
                          }}
                        />
                      </div>
                    </motion.button>
                  </div>
                )}

                {/* Standard Login or LunaKey Input */}
                <AnimatePresence mode="wait">
                  {!useM3UPlaylist && !useLunaKey && (
                    <motion.div
                      animate={{ opacity: 1, height: "auto" }}
                      className="space-y-4"
                      exit={{ opacity: 0, height: 0 }}
                      initial={{ opacity: 0, height: 0 }}
                      key="standard"
                      transition={{ duration: 0.3 }}
                    >
                      <div className="relative">
                        <i className="fas fa-earth-americas absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                        <input
                          className="w-full pl-12 pr-4 py-4 bg-white/95 backdrop-blur-sm rounded-full text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-lg"
                          disabled={loading}
                          name="url"
                          placeholder="https://example.com"
                          type="text"
                          value={formData.url}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="relative">
                        <i className="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                        <input
                          className="w-full pl-12 pr-4 py-4 bg-white/95 backdrop-blur-sm rounded-full text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-lg"
                          disabled={loading}
                          name="username"
                          placeholder="Username"
                          type="text"
                          value={formData.username}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="relative">
                        <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                        <input
                          className="w-full pl-12 pr-4 py-4 bg-white/95 backdrop-blur-sm rounded-full text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-lg"
                          disabled={loading}
                          name="password"
                          placeholder="Password"
                          type="password"
                          value={formData.password}
                          onChange={handleInputChange}
                        />
                      </div>
                    </motion.div>
                  )}

                  {!useM3UPlaylist && useLunaKey && (
                    <motion.div
                      animate={{ opacity: 1, height: "auto" }}
                      className="space-y-4"
                      exit={{ opacity: 0, height: 0 }}
                      initial={{ opacity: 0, height: 0 }}
                      key="lunakey"
                      transition={{ duration: 0.3 }}
                    >
                      <div className="relative">
                        <i className="fas fa-key absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                        <input
                          autoFocus
                          className="w-full pl-12 pr-4 py-4 bg-white/95 backdrop-blur-sm rounded-full text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-lg font-mono tracking-wider text-center"
                          disabled={loading}
                          maxLength={14}
                          placeholder="XXXX-XXXX-XXXX"
                          type="text"
                          value={formData.lunaKey || ""}
                          onChange={(e) => {
                            const value = e.target.value
                              .toUpperCase()
                              .replace(/[^A-Z0-9]/g, "");
                            let formatted = "";

                            for (let i = 0; i < value.length && i < 12; i++) {
                              if (i > 0 && i % 4 === 0) {
                                formatted += "-";
                              }
                              formatted += value[i];
                            }

                            setFormData((prev) => ({
                              ...prev,
                              lunaKey: formatted,
                            }));
                          }}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-6">
                  <motion.button
                    className="flex-1 py-4 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-full shadow-lg transition-all"
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCancelForm}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    className="flex-1 py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-full shadow-lg shadow-purple-600/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                    type="submit"
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin mr-3" />
                        {editingPlaylist ? "UPDATING..." : "ADDING..."}
                      </span>
                    ) : editingPlaylist ? (
                      "UPDATE"
                    ) : (
                      "CONTINUE"
                    )}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modify Dialog */}
      <AnimatePresence>
        {showModifyDialog && selectedForModify && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setShowModifyDialog(false)}
          >
            <motion.div
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gray-800/95 backdrop-blur-md rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-gray-700"
              exit={{ scale: 0.9, opacity: 0 }}
              initial={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-semibold text-white mb-4 text-center">
                Modify Playlist
              </h2>
              <p className="text-gray-300 text-center mb-8">
                Select what you'd like to do for {selectedForModify.name}
              </p>

              <div className="flex gap-3">
                <motion.button
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowModifyDialog(false)}
                >
                  Cancel
                </motion.button>
                <motion.button
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleEditPlaylist}
                >
                  Edit
                </motion.button>
                <motion.button
                  className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDeletePlaylist}
                >
                  Delete
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WebPlayerSwitchPlaylist;
